import React, {
    MutableRefObject,
    ReactElement,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { LanguageCME } from '../../../i18n'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { cmeAdminBaseURL, cmeDetailsByIDURL, getBrandingVideoUploadURL } from '../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../NonReact/constants/REST'
import { IsStringEmpty, isStringNotEmpty, IsVarEmpty, isVarNotEmpty } from '../../NonReact/utils/GlobalFunctions'
import {
    DeepMap,
    FieldError,
    FieldValues,
    FormProvider,
    useFieldArray,
    UseFieldArrayMethods,
    useForm,
    UseFormMethods
} from '../../Vendor/react-hook-form/dist/index.ie11'
import { RESTAPIError } from '../Common/API'
import { requestGetJSON } from '../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../NonReact/common/API/REST/RESTClient'
import { IconName } from '../Common/Components/Icon'
import { LicenceManagement } from '../Common/Components/LicenceManagement/LicenceManagement'
import ModalExitPromptContext, { ModalExitPromptType } from '../Common/Components/Modal/ModalExitPromptContext'
import { SelectOptions } from '../Common/Components/Selects'
import { SelectOption } from '../Common/Components/Selects/Select/Select'
import Tabs from '../Common/Components/Tabs'
import { Tab } from '../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../Common/Constants/HookFormEventNames'
import { DEFAULT_CME_ID } from '../Common/Constants/ServerValues'
import { LanguageContext, MessageContext, ServletPathContext, UserInfoContext } from '../Common/Contexts'
import { MessageContextType } from '../Common/Contexts/MessageContext'
import { ServletPathContextType } from '../Common/Contexts/ServletPathContext'
import { UserInfoContextType } from '../Common/Contexts/UserInfoContext'
import ViewOnlyContext, { ViewOnlyContextType } from '../Common/Contexts/ViewOnlyContext'
import { ReactState } from '../Common/Types/Types'
import TabsContentLoader from '../ConfigureReport/TabsContentLoader'
import CMEDetails from './CMEDetails'
import { ANY_NUMBER_OF_TELSYS_AVAILABLE, CMEModalID, ROOT_CME_ID } from './Constants'
import DomainProfile from './DomainProfile'
import Estate from './Estate'
import {
    ADVANCED,
    BRANDING,
    CAPABILITIES,
    COLOUR,
    cxaTeamsFieldName,
    cxiBroadworksFieldName,
    cxiTeamsFieldName,
    cxiWebexFieldName,
    DETAILS,
    DOMAINPROFILE,
    ESTATE,
    LicenceManagementNameSpacing,
    LICENSEMANAGEMENT,
    LicenseManagementFieldNames,
    PROVISIONING,
    servicepacksNameSpacing
} from './FieldName'
import FooterActions from './FooterActions'
import CMELoadDataMapper, { processBranding } from './Mappers/CMELoadDataMapper'
import CMELoadDefaultDataMapper from './Mappers/CMELoadDefaultDataMapper'
import CMESendDataMapper, { brandingMapper } from './Mappers/CMESendDataMapper'
import Provisioning from './Provisioning/Provisioning'
import {
    CMEAddChangeResponse,
    CMEBranding,
    CMEBrandingBackend,
    CMEDetailsData,
    CMEFormHookData,
    CMEParentData,
    CMEParentItem,
    RealTimeBotsStatus,
    ServicePackItemForm
} from './Types'
import { Advanced } from './Advanced/Advanced'
import { PermissionsUtil } from '../Common/Utils/PermissionsUtil'
import { AdminBackendPermissionNames } from '../Common/Constants/Permissions'
import { ALL_VALUE } from '../Common/API/ItemsGetters'
import { Capabilities } from './Capabilities/Capabilities'
import { Branding } from './Branding/Branding'
import { useFeatureFlag } from '../Common/Hooks/useFeatureFlag'
import { ENABLE_NEW_USER_EXPERIENCE } from '../../ReactRouting/Constants/FeatureFlags'
import {
    addBrandingByType,
    applyBrandingIDToUserSession,
    BrandingType,
    deleteBrandingConfigByType,
    deleteBrandingIDFromUserSession,
    getBrandingConfigByType
} from '../Common/API/BrandingAPI'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { HIDE } from '../../NonReact/constants/class-names'
import { CommonFormContext, FormContextType } from '../Common/Contexts/CommonFormContext'
import { requestMultiPart } from '../Common/API/MultiPartClient'
import { ArrayUtils } from '../../NonReact/utils/ArrayUtils'
import { BrandingSubmission } from './Branding/BrandingSubmission'
import { CXI_FEATURE_FLAG_KEY_FOR_TELSYS } from '../Common/Constants/FeatureFlags'
import { TelSysTypes } from '../TelSysConfiguration/Constants/Constants'
import { APIData } from '../../NonReact/common/Types/Types'
import { getSession } from '../Common/API/CommonAPI'
import { CMEResponse, SessionAPIResponse } from '../../NonReact/Types/Types'
import { getCME } from '../../NonReact/common/API/EntityAPIClient'
import { setModalHelpTopicID } from '../Common/Utils/HelpPortal'
import { DocTopic } from '../Common/Constants/Documentation'

// Sub imports.

/**
 * Component properties type.
 */
type Props = {
    closeModal: () => void
    filterCMEnvironmentID: string
    cmeID?: string
    refresh: () => void
}

type CMEPromises = [Voidable<Nullable<CMEParentData>>, Voidable<Nullable<APIData<CMEDetailsData>>>]

/**
 * Configuration tabs container component.
 * @param closeModal Modal close callback.
 * @param filterCMEnvironmentID Value of CME filter from list page.
 * @param cmeID The ID of the selected cme row from the list page.
 * @param refresh A function to refresh the list page on submission of the form.
 */
const CMETabs: React.FC<Props> = ({ closeModal, filterCMEnvironmentID, cmeID, refresh }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageCME.NAME)

    const servletPath: ServletPathContextType = useContext(ServletPathContext) as string
    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const { displayError }: MessageContextType = useContext(MessageContext)
    const { displayExitPrompt }: ModalExitPromptType = useContext(ModalExitPromptContext)
    const { isViewOnly, setIsViewOnly }: ViewOnlyContextType = useContext(ViewOnlyContext)

    const commonForm: Nullable<FormContextType> = useContext(CommonFormContext)

    const isBrandingEnabled: Nullable<boolean> = useFeatureFlag(ENABLE_NEW_USER_EXPERIENCE)

    // eslint-disable-next-line
    const [isDataLoading, setIsDataLoading]: ReactState<boolean> = useState<boolean>(false)
    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [isBrandingSubmission, setIsBrandingSubmission]: ReactState<boolean> = useState<boolean>(false)
    // Active tab ID.
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(CMEModalID.DETAILS)
    // data
    const [parentCMEData, setParentCMEData]: ReactState<Definable<SelectOptions<string>>> =
        useState<Definable<SelectOptions<string>>>()
    const [botStatus, setBotStatus]: ReactState<Nullable<RealTimeBotsStatus>> = useState<Nullable<RealTimeBotsStatus>>()
    const [isRoot, setIsRoot]: ReactState<boolean> = useState<boolean>(false)
    const [isResetAvailable, setIsResetAvailable]: ReactState<boolean> = useState<boolean>(false)
    const [isPreviewAvailable, setIsPreviewAvailable]: ReactState<boolean> = useState<boolean>(false)
    const activeBrandingTabState: ReactState<string> = useState<string>(COLOUR)
    const [, setActiveBrandingTab]: ReactState<string> = activeBrandingTabState
    const [currentBrandingConfig, setCurrentBrandingConfig]: ReactState<Definable<CMEBranding>> =
        useState<Definable<CMEBranding>>()
    const [currentPreviewBrandingID, setCurrentPreviewBrandingID]: ReactState<Nullable<string>> =
        useState<Nullable<string>>()
    const [currentBrandingID, setCurrentBrandingID]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const isPreviewDisplayedState: ReactState<boolean> = useState<boolean>(false)
    const [, setIsPreviewDisplayed]: ReactState<boolean> = isPreviewDisplayedState
    const isResetDisplayedState: ReactState<boolean> = useState<boolean>(false)
    const isBrandingLoadingState: ReactState<boolean> = useState<boolean>(false)
    const [, setIsBrandingLoading]: ReactState<boolean> = isBrandingLoadingState

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const isAdvancedTabDisplayed: boolean = useMemo(
        (): boolean =>
            isStringNotEmpty(cmeID) &&
            isVarNotEmpty(botStatus) &&
            (userInfo?.Scope?.TelSysID?.length ?? 0) === 1 &&
            userInfo?.Scope?.TelSysID?.[0] === ALL_VALUE &&
            PermissionsUtil.hasPermissions(
                userInfo,
                PermissionsUtil.builder(AdminBackendPermissionNames.TEL_SYS).update().read()
            ),
        [cmeID, botStatus, userInfo]
    )

    const isBrandingConfigEnabled: boolean = useMemo(
        (): boolean =>
            (IsStringEmpty(cmeID) || isVarNotEmpty(currentBrandingConfig)) &&
            !!userInfo?.Permissions?.Admin?.includes(AdminBackendPermissionNames.CME_BRANDING_CONFIG) &&
            !!isBrandingEnabled,
        [userInfo, isBrandingEnabled, cmeID, currentBrandingConfig]
    )

    const isAdmin: boolean = PermissionsUtil.isAdmin(userInfo)

    const userOwnAdminPermissions: AdminBackendPermissionNames[] = useMemo((): AdminBackendPermissionNames[] => {
        const permissions: AdminBackendPermissionNames[] = []

        if (isVarNotEmpty(userInfo)) {
            if (
                !isViewOnly ||
                userInfo.Permissions?.Admin?.includes(AdminBackendPermissionNames.EDIT_OWN_CME_DOMAIN_PROFILE)
            ) {
                permissions.push(AdminBackendPermissionNames.EDIT_OWN_CME_DOMAIN_PROFILE)
            }

            if (
                !isViewOnly ||
                userInfo.Permissions?.Admin?.includes(AdminBackendPermissionNames.EDIT_OWN_CME_PROVISIONING)
            ) {
                permissions.push(AdminBackendPermissionNames.EDIT_OWN_CME_PROVISIONING)
            }

            if (
                !isViewOnly ||
                userInfo.Permissions?.Admin?.includes(AdminBackendPermissionNames.EDIT_OWN_CME_BRANDING)
            ) {
                permissions.push(AdminBackendPermissionNames.EDIT_OWN_CME_BRANDING)
            }
        }

        return permissions
    }, [userInfo, isViewOnly])

    const userHasAnyOwnCMEPermissions: boolean = userOwnAdminPermissions.length > 0

    // Form context.
    const methods: UseFormMethods<CMEFormHookData> = useForm({
        defaultValues: {
            details: {
                description: EMPTY_STRING,
                id: EMPTY_STRING,
                billingid: EMPTY_STRING,
                parentcme: DEFAULT_CME_ID,
                providerids: EMPTY_STRING,
                disableAkixiLogin: false
            },
            domainProfile: {
                domain: EMPTY_STRING,
                serveripaddress: EMPTY_STRING,
                tcpport: 0,
                communicationactive: false,
                fromaddressemail: EMPTY_STRING,
                fromaddressname: EMPTY_STRING,
                companyname: EMPTY_STRING,
                teamsappdownloadurl: EMPTY_STRING,
                cxiClientWindowsUrl: EMPTY_STRING,
                cxiClientMacUrl: EMPTY_STRING,
                searchAndCallUrl: EMPTY_STRING,
                useauthentication: false,
                username: EMPTY_STRING,
                password: EMPTY_STRING,
                cxiAccessCode: EMPTY_STRING
            },
            provisioning: {
                broadworks: {
                    servicepacks: []
                }
            },
            capabilities: {
                maxTelsysCount: ANY_NUMBER_OF_TELSYS_AVAILABLE,
                cxaBroadworks: true,
                cxaWebex: true,
                cxaTeams: true,
                cxiBroadworks: true,
                cxiTeams: true,
                cxiWebex: true,
                legacyTelephony: true,
                legacy: true,
                enterprise: true,
                showUiVersionToggle: false
            },
            licenseManagement: {
                EssentialsCap: EMPTY_STRING,
                ProfessionalCap: EMPTY_STRING,
                EnterpriseCap: EMPTY_STRING,
                CallCentreAgentCap: EMPTY_STRING,
                CallCentreSupervisorCap: EMPTY_STRING,
                CXIntegrationCap: EMPTY_STRING
            },
            ...(isBrandingConfigEnabled ? { branding: { brandingEnabled: true } } : {})
        } as CMEFormHookData,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    const { fields, append, remove }: UseFieldArrayMethods<ServicePackItemForm> = useFieldArray<ServicePackItemForm>({
        control: methods.control,
        name: servicepacksNameSpacing
    })

    const { reset, watch, trigger }: UseFormMethods<CMEFormHookData> = methods

    const [parentCME, setParentCME]: ReactState<Nullable<CMEResponse>> = useState<Nullable<CMEResponse>>()

    const errorHandler = (e: RESTAPIError): void => {
        displayError(e)
        setIsDataLoading(false)
        setIsSubmitting(false)
    }

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => setActiveTabID(newActiveTabID)

    const hasRequested: MutableRefObject<boolean> = useRef<boolean>(false)

    const replaceFormBrandingFromType = (type: BrandingType): Promise<unknown> => {
        if (isStringNotEmpty(cmeID)) {
            setIsBrandingLoading(true)
            return getBrandingConfigByType(cmeID, type)
                .then((previousBrandingData: Nullable<CMEBrandingBackend>): Promise<unknown> => {
                    const newFormData: CMEFormHookData = {
                        ...watch(),
                        branding: processBranding(previousBrandingData ?? {})
                    }

                    /**
                     * The double reset is used to ensure that the form state is correctly updated.
                     * This is a workaround for a known issue with react-hook-form where the form state does not update correctly when reset is called with deeply nested default values.
                     * By calling reset twice, you ensure that the form state is fully updated with the new values.
                     */
                    reset(newFormData)

                    return new Promise((resolve: (value: unknown) => void): void => {
                        setTimeout((): void => {
                            reset(newFormData)
                            resolve(commonForm?.resetDirtyFieldsByNamespace?.(BRANDING))
                        })
                    })
                })
                .catch(displayError)
                .finally((): void => setIsBrandingLoading(false))
        }

        return Promise.reject()
    }

    /**
     * Video uploads are handled separately from the main form submission.
     * General form data is submitted first, then the video files are uploaded.
     *
     * @param brandingGUID - The branding GUID to associate the video files with.
     * @param files - The video files to upload.
     * @param isPreview - Whether the video files are for a preview branding.
     */
    const uploadVideos = (brandingGUID: string, files: [string, File][], isPreview?: boolean): Promise<unknown[]> => {
        const formDatas: Record<string, string | Blob>[] = files.map(
            ([key, file]: [string, File]): Record<string, string | Blob> => ({
                video: file,
                videoKey: key,
                isPreview: Boolean(isPreview).toString()
            })
        )

        return Promise.all(
            formDatas.map(
                (formData: Record<string, string | Blob>): Promise<unknown> =>
                    requestMultiPart(getBrandingVideoUploadURL(brandingGUID), formData)
            )
        )
    }

    const applyPreviewBrandingToTheUser = (previewBrandingID: Nullable<string>): void => {
        setIsBrandingLoading(true)

        applyBrandingIDToUserSession(previewBrandingID ?? EMPTY_STRING)
            .then((): void => window.location.reload())
            .catch(displayError)
            .finally((): void => setIsBrandingLoading(false))
    }

    const replaceFormContentWithPreviousBranding = (): Promise<unknown> =>
        replaceFormBrandingFromType(BrandingType.PREVIOUS)

    const replaceFormContentWithCurrentBranding = (): Promise<unknown> =>
        replaceFormBrandingFromType(BrandingType.CURRENT)

    const replaceFormContentWithPreviewBranding = (): Promise<void> =>
        replaceFormBrandingFromType(BrandingType.PREVIEW).then((): void =>
            applyPreviewBrandingToTheUser(currentPreviewBrandingID)
        )

    const applyPreviewBranding = (): Promise<void> => {
        setIsBrandingSubmission(true)
        setIsSubmitting(true)

        return trigger()
            .then((isValid: boolean): Promise<void> => {
                if (isValid) {
                    const currentBranding: CMEBranding = watch(BRANDING)

                    return brandingMapper(currentBranding).then(
                        ([currentBrandingMappedForBackend, files]: [
                            Definable<CMEBrandingBackend>,
                            [string, File][]
                        ]): Promise<void> => {
                            if (isVarNotEmpty(currentBrandingMappedForBackend)) {
                                return addBrandingByType(
                                    cmeID ?? EMPTY_STRING,
                                    BrandingType.PREVIEW,
                                    currentBrandingMappedForBackend
                                )
                                    .then(
                                        (res: Nullable<string>): Promise<void> =>
                                            uploadVideos(res ?? EMPTY_STRING, files, true).then((): void => {
                                                applyPreviewBrandingToTheUser(res)
                                            })
                                    )
                                    .catch(displayError)
                            }

                            return Promise.reject()
                        }
                    )
                }

                return Promise.reject()
            })
            .finally((): void => {
                setIsBrandingSubmission(false)
                setIsSubmitting(false)
            })
    }

    const areDirtyBrandingFields: boolean = useMemo(
        (): boolean =>
            Object.keys(commonForm?.dirtyFields ?? {})?.some((dirtyField: string): boolean =>
                dirtyField.includes(BRANDING)
            ),
        [JSON.stringify(commonForm?.dirtyFields)]
    )

    const isCxiEnabledForPlatform: Record<TelSysTypes, boolean> = {
        [TelSysTypes.BROADSOFT_BROADWORKS]: useFeatureFlag(
            CXI_FEATURE_FLAG_KEY_FOR_TELSYS[TelSysTypes.BROADSOFT_BROADWORKS],
            false
        ),
        [TelSysTypes.WEBEX_CALLING]: useFeatureFlag(CXI_FEATURE_FLAG_KEY_FOR_TELSYS[TelSysTypes.WEBEX_CALLING], false),
        [TelSysTypes.MS_TEAMS]: useFeatureFlag(CXI_FEATURE_FLAG_KEY_FOR_TELSYS[TelSysTypes.MS_TEAMS], false)
    } as Record<TelSysTypes, boolean>

    const isCxiEnabledOnAnyPlatform: boolean = useMemo(
        () =>
            isCxiEnabledForPlatform[TelSysTypes.BROADSOFT_BROADWORKS] ||
            isCxiEnabledForPlatform[TelSysTypes.WEBEX_CALLING] ||
            isCxiEnabledForPlatform[TelSysTypes.MS_TEAMS],
        [
            isCxiEnabledForPlatform[TelSysTypes.BROADSOFT_BROADWORKS],
            isCxiEnabledForPlatform[TelSysTypes.WEBEX_CALLING],
            isCxiEnabledForPlatform[TelSysTypes.MS_TEAMS]
        ]
    )

    const loadCMEData = useCallback((): void => {
        if (isMounted.current && userInfo && isVarNotEmpty(isBrandingEnabled)) {
            setIsDataLoading(true)
            // Only call this request once when the UserInfo has been retrieved.
            hasRequested.current = true

            const getParentData: Promise<Voidable<Nullable<CMEParentData>>> = requestGetJSON<Nullable<CMEParentData>>(
                cmeAdminBaseURL()
            )
                .then((response: Nullable<CMEParentData>): Nullable<CMEParentData> => {
                    if (response) {
                        const dataTree: CMEParentData['Data'] = response.Data
                        setParentCMEData(
                            dataTree?.Items?.map(
                                (item: CMEParentItem): SelectOption<string> => ({
                                    label: item.Description ?? language.get(LanguageCME.UnknownDescription),
                                    value: item.CMEnvironmentID
                                })
                            )
                        )
                    }

                    return response
                })
                .catch((e: RESTAPIError): void => errorHandler(e))

            if (isStringNotEmpty(cmeID) && cmeID !== 'undefined') {
                const getCMEData: Promise<Voidable<Nullable<APIData<CMEDetailsData>>>> = requestGetJSON<
                    APIData<CMEDetailsData>
                >(cmeDetailsByIDURL(cmeID))
                    .then((response: Nullable<APIData<CMEDetailsData>>): Nullable<APIData<CMEDetailsData>> => response)
                    .catch((e: RESTAPIError): void => errorHandler(e))

                Promise.all([getParentData, getCMEData])
                    .then((response): void => {
                        if (response) {
                            const cmeData: Voidable<Nullable<APIData<CMEDetailsData>>> = response[1]

                            if (cmeData) {
                                // Is view only?
                                const resCMEID: string = cmeData.Data.CMEnvironmentID
                                const isReadOnly: boolean =
                                    !IsStringEmpty(resCMEID) &&
                                    resCMEID !== ROOT_CME_ID &&
                                    userInfo?.Scope.CMEnvironmentID === resCMEID

                                setIsViewOnly(isReadOnly || isViewOnly)

                                setBotStatus(
                                    isVarNotEmpty(cmeData.Data?.BotsDisabledTelsys) &&
                                        isVarNotEmpty(cmeData.Data?.BotsEnabledTelsys)
                                        ? {
                                              disabledTelSysCount: cmeData.Data?.BotsDisabledTelsys,
                                              enabledTelSysCount: cmeData.Data?.BotsEnabledTelsys
                                          }
                                        : undefined
                                )

                                setIsRoot(cmeData.Data.CMEnvironmentID === ROOT_CME_ID)

                                // Set the current branding ID.
                                setCurrentPreviewBrandingID(cmeData?.Data?.PreviewBrandingGUID)
                                setCurrentBrandingID(cmeData?.Data?.BrandingGUID)

                                // Set form state.
                                const formState: CMEFormHookData = CMELoadDataMapper(cmeData)

                                if (isStringNotEmpty(cmeData?.Data?.ParentCMEnvironmentID)) {
                                    getCME(cmeData.Data.ParentCMEnvironmentID, 1000)
                                        .then((cme: Nullable<CMEResponse>) => {
                                            if (isMounted.current && isVarNotEmpty(cme)) {
                                                setParentCME(cme)
                                            }
                                        })
                                        .catch(displayError)
                                }

                                // Is there reset branding data available?
                                setIsResetAvailable(cmeData?.Data?.hasPreviousBrandingConfig ?? false)

                                // Is there preview branding data available?
                                setIsPreviewAvailable(cmeData?.Data?.hasPreviewBrandingConfig ?? false)

                                // Set the current branding config.
                                setCurrentBrandingConfig(cmeData?.Data?.BrandingConfig ?? {})

                                if (cmeID === DEFAULT_CME_ID && isBrandingEnabled) {
                                    if (IsVarEmpty(formState.branding)) {
                                        formState.branding = { brandingEnabled: true }
                                    } else {
                                        formState.branding.brandingEnabled = true
                                    }
                                }

                                // There is a really weird bug where the form state is not being set, if the provisioning is added to the reset.
                                reset({
                                    details: formState.details,
                                    domainProfile: formState.domainProfile,
                                    capabilities: formState.capabilities,
                                    branding: formState.branding
                                })

                                setTimeout((): void => {
                                    reset(formState)

                                    if (
                                        isStringNotEmpty(userInfo?.SessionInfo?.PreviewBrandingID) &&
                                        userInfo?.SessionInfo?.PreviewBrandingID === cmeData?.Data?.PreviewBrandingGUID
                                    ) {
                                        replaceFormBrandingFromType(BrandingType.PREVIEW).then((): void =>
                                            setIsPreviewDisplayed(true)
                                        )
                                    }
                                })
                            }

                            setIsDataLoading(false)
                        }
                    })
                    .catch((e: RESTAPIError): void => errorHandler(e))
            } else {
                // Add new CME section.
                // No CME ID provided meaning we are adding a new CME.

                // Users data.
                const getUserCMEData =
                    userInfo && userInfo.Permissions
                        ? requestGetJSON<Nullable<APIData<CMEDetailsData>>>(
                              cmeDetailsByIDURL(userInfo.Scope.CMEnvironmentID)
                          )
                              .then((response: Nullable<APIData<CMEDetailsData>>) => response)
                              .catch((e: RESTAPIError) => errorHandler(e))
                        : undefined

                Promise.all([getParentData, getUserCMEData])
                    .then((response: CMEPromises): void => {
                        if (response) {
                            const [parentData, userCMEData]: CMEPromises = response
                            if (parentData && userCMEData) {
                                const formState: CMEFormHookData = CMELoadDefaultDataMapper(
                                    userCMEData,
                                    filterCMEnvironmentID
                                )

                                reset(formState)
                                setTimeout((): void => reset(formState))

                                if (isStringNotEmpty(filterCMEnvironmentID)) {
                                    getCME(filterCMEnvironmentID, 1000)
                                        .then((cme: Nullable<CMEResponse>) => {
                                            if (isMounted.current && isVarNotEmpty(cme)) {
                                                setParentCME(cme)
                                            }
                                        })
                                        .catch(displayError)
                                }
                            }

                            setIsDataLoading(false)
                        }
                    })
                    .catch((e: RESTAPIError): void => errorHandler(e))
            }
        }
    }, [cmeID, filterCMEnvironmentID, isMounted, methods, userInfo, isViewOnly, isBrandingEnabled])

    const determineValidationErrors = (formErrors: DeepMap<FieldValues, FieldError>): boolean[] => [
        !!formErrors?.[DETAILS],
        !!formErrors?.[DOMAINPROFILE],
        !!formErrors?.[PROVISIONING],
        !!formErrors?.[LICENSEMANAGEMENT],
        !!formErrors?.[CAPABILITIES],
        !!formErrors?.[BRANDING],
        !!formErrors?.[ESTATE],
        !!formErrors?.[ADVANCED]
    ]

    const hasCxaTeamsCapability: boolean = watch(cxaTeamsFieldName, false)

    const cxiBroadworksValue: boolean = watch(cxiBroadworksFieldName, false)
    const cxiWebexValue: boolean = watch(cxiWebexFieldName, false)
    const cxiTeamsValue: boolean = watch(cxiTeamsFieldName, false)

    const hasCxiCapability: boolean = useMemo(
        (): boolean => cxiBroadworksValue || cxiWebexValue || cxiTeamsValue,
        [cxiBroadworksValue, cxiWebexValue, cxiTeamsValue]
    )

    const validationErrors: boolean[] = determineValidationErrors(methods.errors)

    // List of all tabs settings.
    let tabs: Tab[] = [
        {
            ID: CMEModalID.DETAILS,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(LanguageCME.LanguageCME_DetailsTitle),
                iconName: IconName.REPORT,
                onClick: (): void => {
                    activateTab(CMEModalID.DETAILS)
                    setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                }
            },
            content: (
                <CMEDetails
                    parentCMEData={parentCMEData}
                    cmeID={cmeID}
                    isViewOnly={isViewOnly}
                    setParentCME={setParentCME}
                />
            ),
            hasError: validationErrors[CMEModalID.DETAILS]
        },
        {
            ID: CMEModalID.DOMAIN,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(LanguageCME.DomainsTitle),
                iconName: IconName.LANGUAGE,
                onClick: (): void => {
                    activateTab(CMEModalID.DOMAIN)
                    setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                }
            },
            content: (
                <DomainProfile
                    isViewOnly={
                        !userOwnAdminPermissions.includes(AdminBackendPermissionNames.EDIT_OWN_CME_DOMAIN_PROFILE)
                    }
                    isAdmin={isAdmin}
                    isCxiEnabled={isCxiEnabledOnAnyPlatform}
                    hasCxiCapability={hasCxiCapability}
                    parentCME={parentCME}
                    isNewCME={IsStringEmpty(cmeID)}
                />
            ),
            hasError: validationErrors[CMEModalID.DOMAIN]
        },
        {
            ID: CMEModalID.PROVISIONING,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(LanguageCME.ProvisioningTitle),
                iconName: IconName.PROVISIONING,
                onClick: (): void => {
                    activateTab(CMEModalID.PROVISIONING)
                    setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                }
            },
            content: (
                <Provisioning
                    cmeID={cmeID || ''}
                    servicePackValues={fields}
                    addNewServicePackItem={(servicePackItem: ServicePackItemForm): void => {
                        append(servicePackItem)
                    }}
                    removeServicePackItem={(index: number): void => {
                        remove(index)
                    }}
                    methods={methods}
                    isViewOnly={
                        !userOwnAdminPermissions.includes(AdminBackendPermissionNames.EDIT_OWN_CME_PROVISIONING)
                    }
                />
            ),
            hasError: validationErrors[CMEModalID.PROVISIONING]
        },
        ...(hasCxaTeamsCapability
            ? [
                  {
                      ID: CMEModalID.LICENSE_MANAGEMENT,
                      isDisplayed: true,
                      tabsMenuItem: {
                          title: language.get(LanguageCME.LicenseManagementTitle),
                          iconName: IconName.LICENCES,
                          onClick: (): void => {
                              activateTab(CMEModalID.LICENSE_MANAGEMENT)
                              setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                          }
                      },
                      content: (
                          <LicenceManagement
                              methods={methods}
                              isCxiAvailable={isCxiEnabledOnAnyPlatform && hasCxiCapability}
                              formNames={{
                                  enterprise: LicenceManagementNameSpacing(LicenseManagementFieldNames.ENTERPRISE),
                                  essentials: LicenceManagementNameSpacing(LicenseManagementFieldNames.ESSENTIALS),
                                  professional: LicenceManagementNameSpacing(LicenseManagementFieldNames.PROFESSIONAL),
                                  callCentreAgent: LicenceManagementNameSpacing(
                                      LicenseManagementFieldNames.CALL_CENTRE_AGENT
                                  ),
                                  callCentreSupervisor: LicenceManagementNameSpacing(
                                      LicenseManagementFieldNames.CALL_CENTRE_SUPERVISOR
                                  ),
                                  cxIntegration: LicenceManagementNameSpacing(
                                      LicenseManagementFieldNames.CX_INTEGRATION
                                  )
                              }}
                              isViewOnly={isViewOnly}
                          />
                      ),
                      hasError: validationErrors[CMEModalID.LICENSE_MANAGEMENT]
                  }
              ]
            : []),
        {
            ID: CMEModalID.CAPABILITIES,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(LanguageCME.CapabilitiesTitle),
                iconName: IconName.LOCKED,
                onClick: (): void => {
                    activateTab(CMEModalID.CAPABILITIES)
                    setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                }
            },
            content: (
                <Capabilities
                    cmeID={cmeID}
                    isViewOnly={isViewOnly}
                    isCxiEnabledForPlatform={isCxiEnabledForPlatform}
                    isCxiEnabledOnAnyPlatform={isCxiEnabledOnAnyPlatform}
                    isAdmin={isAdmin}
                    parentCME={parentCME}
                />
            ),
            hasError: validationErrors[CMEModalID.CAPABILITIES]
        },
        ...(isBrandingConfigEnabled
            ? [
                  {
                      ID: CMEModalID.BRANDING,
                      isDisplayed: true,
                      tabsMenuItem: {
                          title: language.get(LanguageCME.BrandingTitle),
                          iconName: IconName.BRANDING,
                          onClick: (): void => {
                              activateTab(CMEModalID.BRANDING)
                              setModalHelpTopicID(DocTopic.CME_BRANDING)
                          }
                      },
                      content: (
                          <Branding
                              viewOnly={
                                  !userOwnAdminPermissions.includes(AdminBackendPermissionNames.EDIT_OWN_CME_BRANDING)
                              }
                              selectedCME={cmeID}
                              isResetAvailable={isResetAvailable}
                              isPreviewAvailable={isPreviewAvailable}
                              areDirtyBrandingFields={areDirtyBrandingFields}
                              replaceFormContentWithPreviousBranding={replaceFormContentWithPreviousBranding}
                              replaceFormContentWithCurrentBranding={replaceFormContentWithCurrentBranding}
                              replaceFormContentWithPreviewBranding={replaceFormContentWithPreviewBranding}
                              applyPreviewBranding={applyPreviewBranding}
                              userBrandingID={userInfo?.SessionInfo?.PreviewBrandingID}
                              existingBranding={currentBrandingConfig}
                              activeBrandingTabState={activeBrandingTabState}
                              currentPreviewBrandingID={currentPreviewBrandingID}
                              isPreviewDisplayedState={isPreviewDisplayedState}
                              isResetDisplayedState={isResetDisplayedState}
                              isLoadingState={isBrandingLoadingState}
                          />
                      ),
                      hasError: validationErrors[CMEModalID.BRANDING]
                  }
              ]
            : [])
    ]

    // Add estate tab if we have a CME ID.
    if (cmeID) {
        tabs = [
            ...tabs,
            {
                ID: CMEModalID.ESTATE,
                isDisplayed: true,
                tabsMenuItem: {
                    title: language.get(LanguageCME.EstateTitle),
                    iconName: IconName.DEVICEHUB,
                    onClick: (): void => {
                        activateTab(CMEModalID.ESTATE)
                        setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                    }
                },
                content: <Estate setIsDataLoading={setIsDataLoading} cmeID={cmeID} errorHandling={errorHandler} />
            },
            {
                ID: CMEModalID.ADVANCED,
                isDisplayed: isAdvancedTabDisplayed,
                tabsMenuItem: {
                    title: language.get(LanguageCME.CME_AdvancedTabTitle),
                    iconName: IconName.WRENCH,
                    onClick: (): void => {
                        activateTab(CMEModalID.ADVANCED)
                        setModalHelpTopicID(DocTopic.ADMINCMENVIRONMENTSPROPS)
                    }
                },
                content: (
                    <Advanced
                        botStatus={botStatus}
                        isRootCME={isRoot}
                        reloadCMEData={loadCMEData}
                        selectedCMEID={cmeID}
                    />
                )
            }
        ]
    }

    useEffect(() => {
        if (!hasRequested.current) {
            loadCMEData()
        }
    }, [userInfo, isBrandingEnabled])

    // eslint-disable-next-line
    const submitCMEConfiguration = async ({
        details,
        domainProfile,
        provisioning,
        capabilities,
        licenseManagement,
        branding
    }: CMEFormHookData): Promise<void> => {
        setIsSubmitting(true)

        getSession()
            .then((sessionResponse: Nullable<SessionAPIResponse>): void => {
                const dirtyFieldNames: string[] = Object.keys(commonForm?.dirtyFields ?? {})
                const hasBrandingDirtyFields: boolean = dirtyFieldNames.some((dirtyField: string): boolean =>
                    dirtyField.includes(BRANDING)
                )
                const userSessionPreviewBrandingID: Nullable<string> = userInfo?.SessionInfo?.PreviewBrandingID

                setIsBrandingSubmission(hasBrandingDirtyFields)

                CMESendDataMapper(
                    {
                        details,
                        domainProfile,
                        provisioning,
                        licenseManagement,
                        capabilities,
                        branding
                    },
                    cmeID,
                    isStringNotEmpty(userSessionPreviewBrandingID),
                    sessionResponse?.Nonce ?? null,
                    isViewOnly ? userOwnAdminPermissions : undefined
                ).then(([body, files]: [Partial<CMEDetailsData>, [string, File][]]): void => {
                    if (isMounted.current) {
                        const addChangeCallback = (res: Nullable<CMEAddChangeResponse>): void => {
                            if (
                                isStringNotEmpty(currentPreviewBrandingID) &&
                                userSessionPreviewBrandingID === currentPreviewBrandingID
                            ) {
                                deleteBrandingIDFromUserSession().then(
                                    (): Promise<Nullable<void>> =>
                                        deleteBrandingConfigByType(cmeID ?? EMPTY_STRING, BrandingType.PREVIEW)
                                            .catch(displayError)
                                            .finally((): void => {
                                                setIsBrandingSubmission(false)
                                                setIsSubmitting(false)
                                                closeModal()
                                                refresh()
                                            })
                                )
                            } else if (ArrayUtils.isEmpty(files)) {
                                closeModal()
                                refresh()
                                setIsSubmitting(false)
                                setIsBrandingSubmission(false)
                            } else {
                                const brandingGUID: Definable<string> =
                                    res?.Data?.BrandingGUID ?? currentBrandingID ?? EMPTY_STRING

                                uploadVideos(brandingGUID ?? EMPTY_STRING, files)
                                    .then((): void => {
                                        closeModal()
                                        refresh()
                                    })
                                    .catch(displayError)
                                    .finally((): void => {
                                        setIsSubmitting(false)
                                        setIsBrandingSubmission(false)
                                    })
                            }
                        }

                        // Which request type should It use.
                        if (cmeID) {
                            // Editing an existing CME.
                            requestJSON<CMEAddChangeResponse>(cmeDetailsByIDURL(cmeID), {
                                method: RESTMethods.PUT,
                                body,
                                errorOnNoContent: false
                            })
                                .then(addChangeCallback)
                                .catch((e: RESTAPIError): void => errorHandler(e))
                        } else {
                            // Creating a new CME.
                            requestJSON<CMEAddChangeResponse>(cmeAdminBaseURL(), {
                                method: RESTMethods.POST,
                                body,
                                errorOnNoContent: false
                            })
                                .then(addChangeCallback)
                                .catch((e: RESTAPIError): void => errorHandler(e))
                        }
                    }
                })
            })
            .catch(errorHandler)
    }

    const invalidFormSubmit = (errors: DeepMap<FieldValues, FieldError>): void => {
        if (!methods.formState.isValid) {
            const firstValidationErrorIndex: number = determineValidationErrors(errors).findIndex(
                (validationError: boolean): boolean => validationError
            )

            setActiveTabID(firstValidationErrorIndex)

            if (firstValidationErrorIndex === CMEModalID.BRANDING) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                const brandingErrors: Nullable<string> = Object.keys(errors?.[BRANDING] ?? {}).find((): boolean => true)

                setActiveBrandingTab(brandingErrors ?? COLOUR)
            }
        }
    }

    return (
        <div className='configuration-tabs'>
            {isDataLoading || (isSubmitting && !isBrandingSubmission) ? <TabsContentLoader /> : <></>}
            {isSubmitting && isBrandingSubmission ? <BrandingSubmission /> : <></>}
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={methods.handleSubmit(submitCMEConfiguration, invalidFormSubmit)}
                    className={isDataLoading || isSubmitting ? HIDE : EMPTY_STRING}
                >
                    <Tabs tabs={tabs} activeTabID={activeTabID} />
                    <FooterActions
                        isSubmitDisabled={validationErrors.some((ve: boolean): boolean => ve)}
                        closeModal={(): void => displayExitPrompt()}
                        servletPath={servletPath}
                        isViewOnly={isViewOnly && !userHasAnyOwnCMEPermissions}
                    />
                </form>
            </FormProvider>
        </div>
    )
}

export default CMETabs
